// S1 Product list (/catalogue/products) — Module 01 §4, on the revision-6
// register (design-system spec §3.3/§3.6/§3.9, D-107).
//
// One page-head row (breadcrumbs + the ONE primary action), one Card holding a
// filter row and the §3.3 `DataTable`: SKU, name, kind, base UoM, status,
// attribute chips (mm² + colour swatch) and standard cost — the cost column is
// omitted entirely when the API returns no cost field (§7 redaction). Cursor
// pagination 50/page rides the table's own footer; bulk activate / discontinue
// is a selection bar inside the card; a template-picker-first "New product"
// opens the create panel.
//
// The phone shape is the DataTable's built-in collapse contract (≤768 px: each
// row becomes a labelled card), which replaced a hand-rolled second `<ul>` of
// mobile cards. That duplicate rendered every SKU TWICE in the DOM — two
// certified walkthroughs carry a comment about disambiguating it. One row, one
// place, one truth.
//
// Every request state is explicit (§3.9): skeleton while loading, retry card
// carrying the request id on error, one directing sentence + the next action
// when empty.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Card } from "../../../../components/card";
import { FilterBar, type FilterChip } from "../../../../components/filter-bar";
import { useDesktop } from "../../../../components/use-desktop";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Select } from "../../../../components/select";
import { Banner, toast } from "../../../../components/toast";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { StatusBadge } from "../../../../components/status-badge";
import type { Product } from "../../../../lib/api";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf, RequestError } from "../../../../lib/request";
import { CostValue, costFieldPresent } from "../components/cost-display";
import {
  useAttributeSchemas,
  useProducts,
  useUoms,
  type ProductFilters,
} from "../hooks/catalogue-queries";
import { useCataloguePermissions } from "../hooks/permissions";
import { ProductAttributeChips } from "./products/attribute-chips";
import { ProductCreatePanel } from "./products/product-create-panel";
import {
  ATTR_OPS,
  buildAttrPredicates,
  hasActiveFilters,
  MAKE_OR_BUY,
  PRODUCT_STATUSES,
  type AttrOp,
} from "./products/product-filters";
import { parseUiHints } from "../components/ui-hints";
import { useBulkLifecycle } from "./products/use-product-actions";
import { PRODUCT_KINDS } from "../../../../lib/api";
import "../catalogue.css";

interface FilterState {
  q: string;
  kind: string;
  status: string;
  schemaCode: string;
  makeOrBuy: string;
  /** The structured attribute predicate (key chosen from the schema, not typed). */
  attrKey: string;
  attrOp: AttrOp;
  attrValue: string;
}

const EMPTY_FILTERS: FilterState = {
  q: "",
  kind: "",
  status: "",
  schemaCode: "",
  makeOrBuy: "",
  attrKey: "",
  attrOp: "eq",
  attrValue: "",
};

const PAGE_SIZE = 50;

export function ProductsListScreen({
  /** EMBEDDED (the module-02 catalogue step renders this screen inside the
   * wizard, never a fork of it): the page head keeps its title + primary
   * action but drops the parent crumb, because a "Catalogue /" link out of the
   * wizard mid-step is a way to lose your place, not a way to navigate. */
  embedded = false,
  /** Rows a page asks the server for. The catalogue step passes fewer than
   * the screen's own 50, so the wizard step, which sits under the step's
   * progress card, stays within the declutter gate's four screens at 1440 x
   * 900 (design-system §3.17, W-SWEEP). */
  pageSize = PAGE_SIZE,
}: { embedded?: boolean; pageSize?: number } = {}) {
  const { t, i18n } = useTranslation();
  const desktop = useDesktop();
  const navigate = useNavigate();
  const crumbs = embedded ? [] : [{ label: t("nav.catalogue"), to: "/catalogue/products" }];

  // The topbar's global search lands here with ?q= (UX-0, design-system spec
  // §3.1): it seeds the free-text filter, and a NEW search while already on
  // this screen re-seeds it (the effect below), resetting the cursor.
  const urlSearch: { q?: string } = useSearch({ strict: false });
  const [filterState, setFilterState] = useState<FilterState>(() =>
    urlSearch.q !== undefined ? { ...EMPTY_FILTERS, q: urlSearch.q } : EMPTY_FILTERS,
  );
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([undefined]);

  useEffect(() => {
    if (urlSearch.q === undefined) return;
    setFilterState((prev) => (prev.q === urlSearch.q ? prev : { ...prev, q: urlSearch.q ?? "" }));
    setCursorHistory([undefined]);
  }, [urlSearch.q]);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkNotice, setBulkNotice] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);

  const currentCursor = cursorHistory[cursorHistory.length - 1];

  const attributes = useMemo(
    () =>
      buildAttrPredicates({
        key: filterState.attrKey,
        op: filterState.attrOp,
        value: filterState.attrValue,
      }),
    [filterState.attrKey, filterState.attrOp, filterState.attrValue],
  );

  const filters = useMemo<ProductFilters>(() => {
    const next: ProductFilters = { limit: pageSize };
    if (currentCursor !== undefined) next.cursor = currentCursor;
    if (filterState.q.trim().length > 0) next.q = filterState.q.trim();
    if (filterState.kind.length > 0) next.kind = filterState.kind;
    if (filterState.status.length > 0) next.status = filterState.status;
    if (filterState.schemaCode.length > 0) next.schema_code = filterState.schemaCode;
    if (filterState.makeOrBuy.length > 0) next.make_or_buy = filterState.makeOrBuy;
    if (Object.keys(attributes).length > 0) next.attributes = attributes;
    return next;
  }, [filterState, currentCursor, attributes, pageSize]);

  const products = useProducts(filters);
  const uoms = useUoms();
  const schemas = useAttributeSchemas();
  const bulk = useBulkLifecycle();
  const perms = useCataloguePermissions();

  // §7 lifecycle gating — the server is the backstop, but these hide controls a
  // role cannot use. `write` also covers procurement (raw/consumable-scoped
  // server-side); `activate`/`discontinue` are distinct codes (planner activates,
  // facility_admin/group_admin discontinue).
  const canCreate = perms.has("catalogue.product.write");
  const canActivate = perms.has("catalogue.product.activate");
  const canDiscontinue = perms.has("catalogue.product.discontinue");

  // 401 → sign-in, matching the P0 products screen.
  useEffect(() => {
    if (products.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [products.error, navigate]);

  const uomCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of uoms.data?.items ?? []) map.set(u.id, u.code);
    return map;
  }, [uoms.data]);

  const schemaById = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    for (const s of schemas.data?.items ?? []) map.set(s.id, s.ui_hints);
    return map;
  }, [schemas.data]);

  /* THE ATTRIBUTE VOCABULARY, made discoverable. Every field of every schema
   * the tenant has (narrowed to the chosen schema when the schema filter is
   * set), keyed by the raw attribute key and LABELLED in the user's language
   * from the same `ui_hints` the chips and the form now read. This is what
   * replaced "type the snake_case key you were never shown". */
  const attrOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const s of schemas.data?.items ?? []) {
      if (filterState.schemaCode.length > 0 && s.code !== filterState.schemaCode) continue;
      const hints = parseUiHints(s.ui_hints, i18n.language);
      for (const key of hints.order) {
        if (byKey.has(key)) continue;
        const unit = hints.units[key];
        const label = hints.labels[key] ?? key;
        byKey.set(key, unit !== undefined ? `${label} (${unit})` : label);
      }
    }
    return [...byKey.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [schemas.data, filterState.schemaCode, i18n.language]);

  function updateFilter(patch: Partial<FilterState>) {
    setFilterState((prev) => ({ ...prev, ...patch }));
    setCursorHistory([undefined]);
    setSelection(new Set());
    setBulkNotice(null);
  }

  function clearFilters() {
    setFilterState(EMPTY_FILTERS);
    setCursorHistory([undefined]);
    setSelection(new Set());
    setBulkNotice(null);
  }

  function toggleSelect(id: string) {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const items = products.data?.items ?? [];
  const nextCursor = products.data?.next_cursor ?? null;
  const showCost = costFieldPresent(items, "standard_cost_minor");

  const selectedItems = items.filter((p) => selection.has(p.id));
  const canBulkActivate = selectedItems.some((p) => p.status === "draft");
  const canBulkDiscontinue = selectedItems.some((p) => p.status === "active");
  const allSelected = items.length > 0 && items.every((p) => selection.has(p.id));

  function toggleSelectAll() {
    setSelection(allSelected ? new Set() : new Set(items.map((p) => p.id)));
  }

  /* A bulk lifecycle change confirms ONCE, inline, naming what happened — the
   * §3.8 rule. A partial result is a `bad` banner, because "17 changed, 3
   * refused" is not a success the user can walk away from. */
  function runBulk(action: "activate" | "discontinue") {
    const ids = [...selection];
    if (ids.length === 0) return;
    setBulkNotice(null);
    bulk.mutate(
      { ids, action },
      {
        onSuccess: (result) => {
          setBulkNotice(
            result.failed === 0
              ? { kind: "ok", text: t("catalogue.productsList.bulk.done", { count: result.ok }) }
              : {
                  kind: "bad",
                  text: t("catalogue.productsList.bulk.partial", {
                    ok: result.ok,
                    failed: result.failed,
                  }),
                },
          );
          setSelection(new Set());
        },
        onError: (err: unknown) => {
          setBulkNotice({
            kind: "bad",
            text:
              err instanceof Error ? err.message : t("catalogue.productsList.bulk.failedFallback"),
          });
        },
      },
    );
  }

  // The chips for the filters behind the panel (the row's own two show their
  // value where they sit) and the count the Filters button carries.
  const chipLabel = (filter: string, value: string): string =>
    t("catalogue.productsList.filters.chip", { filter, value });
  const filterChips: FilterChip[] = [];
  // Below 769 px kind and status move into the sheet too, so they get chips.
  if (!desktop && filterState.kind.length > 0) {
    filterChips.push({
      key: "kind",
      label: chipLabel(t("catalogue.productsList.filters.kind"), t(`kind.${filterState.kind}`)),
      onRemove: () => {
        updateFilter({ kind: "" });
      },
    });
  }
  if (!desktop && filterState.status.length > 0) {
    filterChips.push({
      key: "status",
      label: chipLabel(
        t("catalogue.productsList.filters.status"),
        t(`catalogue.status.${filterState.status}`),
      ),
      onRemove: () => {
        updateFilter({ status: "" });
      },
    });
  }
  if (filterState.schemaCode.length > 0) {
    filterChips.push({
      key: "schema",
      label: chipLabel(
        t("catalogue.productsList.filters.schema"),
        schemas.data?.items.find((s) => s.code === filterState.schemaCode)?.name ??
          filterState.schemaCode,
      ),
      onRemove: () => {
        updateFilter({ schemaCode: "" });
      },
    });
  }
  if (filterState.makeOrBuy.length > 0) {
    filterChips.push({
      key: "makeOrBuy",
      label: chipLabel(
        t("catalogue.productsList.filters.makeOrBuy"),
        t(`catalogue.productsList.makeOrBuy.${filterState.makeOrBuy}`),
      ),
      onRemove: () => {
        updateFilter({ makeOrBuy: "" });
      },
    });
  }
  if (filterState.attrKey.length > 0 && filterState.attrValue.trim().length > 0) {
    filterChips.push({
      key: "attribute",
      label: chipLabel(
        attrOptions.find(([key]) => key === filterState.attrKey)?.[1] ?? filterState.attrKey,
        filterState.attrValue.trim(),
      ),
      onRemove: () => {
        updateFilter({ attrKey: "", attrValue: "" });
      },
    });
  }
  const moreCount = filterChips.length;

  const filtersActive = hasActiveFilters({
    q: filterState.q,
    kind: filterState.kind,
    status: filterState.status,
    schemaCode: filterState.schemaCode,
    makeOrBuy: filterState.makeOrBuy,
    attributes,
  });

  const columns: DataTableColumn<Product>[] = [
    {
      key: "select",
      header: t("catalogue.productsList.col.select"),
      label: t("catalogue.productsList.col.select"),
      /* The select-all lives IN the column header, where §3.3 puts it — not in
       * a band above the table. A separate "Select all" strip is a row that
       * does not earn its place, and it pushed the first product 40 px down
       * the page on every visit. */
      headerNode: (
        <input
          type="checkbox"
          checked={allSelected}
          aria-label={t("catalogue.productsList.bulk.selectAll")}
          onChange={toggleSelectAll}
        />
      ),
      render: (p) => (
        <span className="cat-select-cell">
          <input
            type="checkbox"
            checked={selection.has(p.id)}
            aria-label={p.sku}
            onChange={() => {
              toggleSelect(p.id);
            }}
          />
        </span>
      ),
    },
    {
      key: "sku",
      kind: "id",
      header: t("catalogue.productsList.col.sku"),
      id: true,
      render: (p) => (
        <button
          type="button"
          className="ds-lnk"
          data-testid="product-sku"
          onClick={() => {
            void navigate({ to: "/catalogue/products/$id", params: { id: p.id } });
          }}
        >
          {p.sku}
        </button>
      ),
    },
    { key: "name", header: t("catalogue.productsList.col.name"), render: (p) => p.name },
    {
      key: "kind",
      header: t("catalogue.productsList.col.kind"),
      render: (p) => t(`kind.${p.kind}`),
    },
    {
      key: "baseUom",
      header: t("catalogue.productsList.col.baseUom"),
      render: (p) => (p.base_uom_id.length > 0 ? (uomCode.get(p.base_uom_id) ?? "—") : "—"),
    },
    {
      key: "status",
      header: t("catalogue.productsList.col.status"),
      render: (p) => <StatusBadge status={p.status} />,
    },
    {
      key: "attributes",
      header: t("catalogue.productsList.col.attributes"),
      render: (p) => (
        <ProductAttributeChips
          attributes={p.attributes}
          uiHints={
            p.attribute_schema_id !== null ? schemaById.get(p.attribute_schema_id) : undefined
          }
        />
      ),
    },
    ...(showCost
      ? [
          {
            key: "cost",
            kind: "money" as const,
            header: t("catalogue.productsList.col.cost"),
            numeric: true,
            // A column of dashes says nothing: it shows once a row on the
            // page carries a cost (audit #41).
            presence: (rows: readonly Product[]) =>
              rows.some((r) => r.standard_cost_minor !== null),
            render: (p: Product) => (
              <CostValue
                minor={p.standard_cost_minor}
                currency={p.currency}
                priceUnitQty={p.price_unit_qty}
              />
            ),
          },
        ]
      : []),
  ];

  const failed = products.error !== null && !(products.error instanceof UnauthorizedError);
  /* A 4xx that is not 401 means the SERVER READ THE REQUEST AND REFUSED IT —
   * a filter it will not accept. 5xx / network failures are the retryable
   * class. 429 is excluded: "come back in a moment" IS retryable. */
  const rejected =
    products.error instanceof RequestError &&
    products.error.httpStatus >= 400 &&
    products.error.httpStatus < 500 &&
    products.error.httpStatus !== 429;

  /* ---- the §5 zero-CLS gate ------------------------------------------------
   * This screen's LAYOUT is decided by reads, not just its content: the schema
   * filter's intrinsic width comes from the schema NAMES, the base-unit column
   * from the UoM codes, and whether a Standard-cost column exists at all from
   * the products themselves (§7 redaction). Painting the card first and letting
   * those three settle re-flowed the filter row and every table column
   * sideways — measured 0.0207 on the replica.
   *
   * So the content region gates on all three reads and reveals ATOMICALLY: a
   * reserving skeleton until they have SETTLED (fetched OR failed — fail-closed
   * absence, never an eternal skeleton), then a `key` swap that remounts the
   * subtree, so nothing painted early can move. Filter changes AFTER load go
   * through the DataTable's own loading state, which is not a load-time shift.
   *
   * The reveal LATCHES. `isFetched` belongs to one query key and every filter
   * change is a new key, so an unlatched gate fell back to the skeleton on each
   * change and the key swap remounted the filter row: the Filters panel closed
   * after one keystroke and the search box lost focus (the ViewsRegion rule). */
  const settled = products.isFetched && uoms.isFetched && schemas.isFetched;
  const [revealed, setRevealed] = useState(settled);
  if (settled && !revealed) setRevealed(true);
  const ready = settled || revealed;
  if (!ready) {
    return (
      <section className="products-list cat-screen" key="products-loading">
        <PageHeader title={t("catalogue.productsList.title")} crumbs={crumbs} />
        <Card title={t("catalogue.productsList.title")} data-testid="products-loading">
          <TableSkeleton rows={8} cols={7} label={t("catalogue.productsList.loading")} />
        </Card>
      </section>
    );
  }

  return (
    <section className="products-list cat-screen" key="products-ready">
      <PageHeader
        title={t("catalogue.productsList.title")}
        crumbs={crumbs}
        actions={
          canCreate ? (
            <Button
              variant="primary"
              // Locale-independent handle: walkthroughs must be able to steer
              // this screen in any UI language (M01-S02 runs it in ru).
              data-testid="new-product"
              onClick={() => {
                setCreateOpen(true);
              }}
            >
              {t("catalogue.productsList.new")}
            </Button>
          ) : undefined
        }
      />

      {bulkNotice !== null ? (
        <Banner
          kind={bulkNotice.kind}
          data-testid="bulk-result"
          dismissLabel={t("catalogue.state.dismiss")}
          onDismiss={() => {
            setBulkNotice(null);
          }}
        >
          {bulkNotice.text}
        </Banner>
      ) : null}

      {/* One filter row (rev-7 FilterBar, D-445): search, kind and status in
          the row, the rest behind Filters, applied filters as chips. It used
          to sit in the card's head and wrapped an orphan "Any attribute"
          select onto a second line (audit #41). */}
      <FilterBar
        data-testid="products-filters"
        search={{
          value: filterState.q,
          label: t("catalogue.productsList.searchLabel"),
          placeholder: t("catalogue.productsList.searchPlaceholder"),
          onChange: (value) => {
            updateFilter({ q: value });
          },
        }}
        filters={{
          inline: (
            <>
              <Select
                aria-label={t("catalogue.productsList.filters.kind")}
                value={filterState.kind}
                onChange={(e) => {
                  updateFilter({ kind: e.target.value });
                }}
              >
                <option value="">{t("catalogue.productsList.filters.anyKind")}</option>
                {PRODUCT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`kind.${k}`)}
                  </option>
                ))}
              </Select>
              <Select
                aria-label={t("catalogue.productsList.filters.status")}
                value={filterState.status}
                onChange={(e) => {
                  updateFilter({ status: e.target.value });
                }}
              >
                <option value="">{t("catalogue.productsList.filters.anyStatus")}</option>
                {PRODUCT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`catalogue.status.${s}`)}
                  </option>
                ))}
              </Select>
            </>
          ),
          more: (
            <>
              <Select
                aria-label={t("catalogue.productsList.filters.schema")}
                value={filterState.schemaCode}
                onChange={(e) => {
                  updateFilter({ schemaCode: e.target.value });
                }}
              >
                <option value="">{t("catalogue.productsList.filters.anySchema")}</option>
                {(schemas.data?.items ?? []).map((s) => (
                  <option key={s.id} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </Select>
              <Select
                aria-label={t("catalogue.productsList.filters.makeOrBuy")}
                value={filterState.makeOrBuy}
                onChange={(e) => {
                  updateFilter({ makeOrBuy: e.target.value });
                }}
              >
                <option value="">{t("catalogue.productsList.filters.anyMakeOrBuy")}</option>
                {MAKE_OR_BUY.map((m) => (
                  <option key={m} value={m}>
                    {t(`catalogue.productsList.makeOrBuy.${m}`)}
                  </option>
                ))}
              </Select>
              {attrOptions.length > 0 ? (
                <span className="cat-attr-filter" data-testid="attr-filter">
                  <Select
                    aria-label={t("catalogue.productsList.filters.attribute")}
                    value={filterState.attrKey}
                    onChange={(e) => {
                      updateFilter({ attrKey: e.target.value });
                    }}
                  >
                    <option value="">{t("catalogue.productsList.filters.anyAttribute")}</option>
                    {attrOptions.map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </Select>
                  {filterState.attrKey.length > 0 ? (
                    <>
                      <Select
                        aria-label={t("catalogue.productsList.filters.attributeOp")}
                        value={filterState.attrOp}
                        onChange={(e) => {
                          updateFilter({ attrOp: e.target.value as AttrOp });
                        }}
                      >
                        {ATTR_OPS.map((op) => (
                          <option key={op} value={op}>
                            {t(`catalogue.productsList.filters.op.${op}`)}
                          </option>
                        ))}
                      </Select>
                      <label className="cat-search">
                        <span className="visually-hidden">
                          {t("catalogue.productsList.filters.attributeValue")}
                        </span>
                        <input
                          type="text"
                          value={filterState.attrValue}
                          placeholder={t("catalogue.productsList.filters.attributeValue")}
                          onChange={(e) => {
                            updateFilter({ attrValue: e.target.value });
                          }}
                        />
                      </label>
                    </>
                  ) : null}
                </span>
              ) : null}
            </>
          ),
          label:
            moreCount === 0
              ? t("catalogue.productsList.filters.more")
              : t("catalogue.productsList.filters.moreCount", { count: moreCount }),
          title: t("catalogue.productsList.filters.title"),
          doneLabel: t("catalogue.productsList.filters.done"),
        }}
        chips={filterChips}
        removeLabel={(chip) => t("catalogue.productsList.filters.remove", { filter: chip.label })}
        actions={
          filtersActive ? (
            <Button size="sm" onClick={clearFilters} data-testid="clear-filters">
              {t("catalogue.productsList.filters.clear")}
            </Button>
          ) : null
        }
      />

      <Card
        title={t("catalogue.productsList.title")}
        count={items.length}
        data-testid="products-card"
      >
        {selection.size > 0 ? (
          <div className="ds-cpad">
            <div
              className="cat-bulk"
              role="region"
              aria-label={t("catalogue.productsList.bulk.regionLabel")}
              data-testid="bulk-bar"
            >
              <span className="cat-bulk-count">
                {t("catalogue.productsList.bulk.selectedCount", { count: selection.size })}
              </span>
              {canActivate ? (
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!canBulkActivate}
                  busy={bulk.isPending}
                  busyLabel={t("catalogue.productsList.bulk.running")}
                  onClick={() => {
                    runBulk("activate");
                  }}
                >
                  {t("catalogue.productsList.bulk.activate")}
                </Button>
              ) : null}
              {canDiscontinue ? (
                <Button
                  size="sm"
                  disabled={!canBulkDiscontinue || bulk.isPending}
                  onClick={() => {
                    runBulk("discontinue");
                  }}
                >
                  {t("catalogue.productsList.bulk.discontinue")}
                </Button>
              ) : null}
              <Button
                size="sm"
                className="cat-row-end"
                onClick={() => {
                  setSelection(new Set());
                }}
              >
                {t("catalogue.productsList.bulk.clear")}
              </Button>
            </div>
          </div>
        ) : null}

        <DataTable
          columns={columns}
          rows={items}
          rowKey={(p) => p.id}
          rowTestId="product-row"
          caption={t("catalogue.productsList.title")}
          loading={products.isPending || products.error instanceof UnauthorizedError}
          loadingLabel={t("catalogue.productsList.loading")}
          error={
            failed ? (
              /* A REFUSED QUERY IS NOT A BROKEN SERVER. When the request itself
               * was rejected (4xx — a filter the API would not accept), the
               * recovery is to change the filters, not to retry an identical
               * request that must fail identically. Offering "Retry" there is
               * an inoperable action, and calling it "Could not load products"
               * blames the system for the user's input. */
              rejected ? (
                <EmptyState
                  title={t("catalogue.productsList.filterRejectedTitle")}
                  body={t("catalogue.productsList.filterRejectedBody")}
                  ctaLabel={t("catalogue.productsList.clearFilters")}
                  onCta={clearFilters}
                />
              ) : (
                <ErrorState
                  title={t("catalogue.productsList.errorTitle")}
                  detail={products.error instanceof Error ? products.error.message : undefined}
                  retryLabel={t("catalogue.state.retry")}
                  onRetry={() => void products.refetch()}
                  requestId={requestIdOf(products.error)}
                  requestIdLabel={t("catalogue.state.requestId")}
                />
              )
            ) : undefined
          }
          empty={
            filtersActive ? (
              <EmptyState
                title={t("catalogue.productsList.noMatchTitle")}
                body={t("catalogue.productsList.noMatchBody")}
                ctaLabel={t("catalogue.productsList.clearFilters")}
                onCta={clearFilters}
              />
            ) : (
              <EmptyState
                title={t("catalogue.productsList.emptyTitle")}
                body={t("catalogue.productsList.emptyBody")}
                ctaLabel={canCreate ? t("catalogue.productsList.emptyCta") : undefined}
                onCta={
                  canCreate
                    ? () => {
                        setCreateOpen(true);
                      }
                    : undefined
                }
              />
            )
          }
          pager={
            items.length > 0
              ? {
                  range: t("catalogue.productsList.page.showing", { count: items.length }),
                  page: cursorHistory.length,
                  onPage: (page) => {
                    if (page < cursorHistory.length) {
                      setCursorHistory((h) => (h.length > 1 ? h.slice(0, -1) : h));
                    } else if (nextCursor !== null) {
                      setCursorHistory((h) => [...h, nextCursor]);
                    }
                    setSelection(new Set());
                    setBulkNotice(null);
                  },
                  hasPrev: cursorHistory.length > 1,
                  hasNext: nextCursor !== null,
                  prevLabel: t("catalogue.productsList.page.prev"),
                  nextLabel: t("catalogue.productsList.page.next"),
                }
              : undefined
          }
        />
      </Card>

      {createOpen ? (
        <ProductCreatePanel
          onClose={() => {
            setCreateOpen(false);
          }}
          onCreated={(product) => {
            setCreateOpen(false);
            /* §3.8 — the module's HEADLINE mutation confirmed nothing: the
             * panel closed, the URL changed, and the user was left to infer
             * success from the absence of an error. A toast, because the
             * navigation below unmounts this screen; it names the SKU and the
             * state the product is actually in (draft, not yet usable). */
            toast.success(t("catalogue.productsList.create.created", { sku: product.sku }));
            void navigate({ to: "/catalogue/products/$id", params: { id: product.id } });
          }}
        />
      ) : null}
    </section>
  );
}
